import { lazy } from 'react';
import { ROUTES, PUBLIC_ROUTES } from '@/constants';
import {
  PortalTournamentsRoute,
  PortalTeamsRoute,
  PortalPlayersRoute,
  PortalGroundsRoute,
  PortalGalleryRoute,
  PortalSponsorsRoute,
} from './portalRoutes';

const HomePage = lazy(() => import('@/pages/PublicWebsite/HomePage'));
const AboutPage = lazy(() => import('@/pages/PublicWebsite/AboutPage'));
const PublicTournamentsPage = lazy(() => import('@/pages/PublicWebsite/PublicTournamentsPage'));
const PublicFixturesPage = lazy(() => import('@/pages/PublicWebsite/PublicFixturesPage'));
const PublicResultsPage = lazy(() => import('@/pages/PublicWebsite/PublicResultsPage'));
const PublicLivePage = lazy(() => import('@/pages/PublicWebsite/PublicLivePage'));
const PublicTeamsPage = lazy(() => import('@/pages/PublicWebsite/PublicTeamsPage'));
const PublicTeamProfilePage = lazy(() => import('@/pages/PublicWebsite/PublicTeamProfilePage'));
const PublicPlayersPage = lazy(() => import('@/pages/PublicWebsite/PublicPlayersPage'));
const PublicPlayerProfilePage = lazy(() => import('@/pages/PublicWebsite/PublicPlayerProfilePage'));
const PublicGroundsPage = lazy(() => import('@/pages/PublicWebsite/PublicGroundsPage'));
const PublicGroundDetailsPage = lazy(() => import('@/pages/PublicWebsite/PublicGroundDetailsPage'));
const PublicGalleryPage = lazy(() => import('@/pages/PublicWebsite/PublicGalleryPage'));
const PublicAlbumPage = lazy(() => import('@/pages/PublicWebsite/PublicAlbumPage'));
const PublicSponsorsPage = lazy(() => import('@/pages/PublicWebsite/PublicSponsorsPage'));
const ContactPage = lazy(() => import('@/pages/PublicWebsite/ContactPage'));
const PrivacyPage = lazy(() => import('@/pages/PublicWebsite/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/PublicWebsite/TermsPage'));
const PublicNotFoundPage = lazy(() => import('@/pages/PublicWebsite/PublicNotFoundPage'));
const PublicMatchPage = lazy(() => import('@/pages/Public/PublicMatchPage'));
const PublicTournamentPage = lazy(() => import('@/pages/Public/PublicTournamentPage'));
const DesignSystemPage = lazy(() => import('@/pages/DesignSystem/DesignSystemPage'));

const LoginPage = lazy(() => import('@/pages/Authentication/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/Dashboard/DashboardPage'));
const TournamentListPage = lazy(() => import('@/pages/Tournament/TournamentListPage'));
const TournamentWizardPage = lazy(() => import('@/pages/Tournament/TournamentWizardPage'));
const TournamentDetailsPage = lazy(() => import('@/pages/Tournament/TournamentDetailsPage'));
const TournamentEditPage = lazy(() => import('@/pages/Tournament/TournamentEditPage'));
const TournamentSettingsPage = lazy(() => import('@/pages/Tournament/TournamentSettingsPage'));
const TeamListPage = lazy(() => import('@/pages/Teams/TeamListPage'));
const TeamWizardPage = lazy(() => import('@/pages/Teams/TeamWizardPage'));
const TeamDetailsPage = lazy(() => import('@/pages/Teams/TeamDetailsPage'));
const TeamEditPage = lazy(() => import('@/pages/Teams/TeamEditPage'));
const TeamStatisticsPage = lazy(() => import('@/pages/Teams/TeamStatisticsPage'));
const TeamGalleryPage = lazy(() => import('@/pages/Teams/TeamGalleryPage'));
const PlayerListPage = lazy(() => import('@/pages/Players/PlayerListPage'));
const PlayerCreatePage = lazy(() => import('@/pages/Players/PlayerCreatePage'));
const PlayerDetailsPage = lazy(() => import('@/pages/Players/PlayerDetailsPage'));
const PlayerEditPage = lazy(() => import('@/pages/Players/PlayerEditPage'));
const PlayerStatisticsPage = lazy(() => import('@/pages/Players/PlayerStatisticsPage'));
const MatchListPage = lazy(() => import('@/pages/Matches/MatchListPage'));
const MatchCreatePage = lazy(() => import('@/pages/Matches/MatchCreatePage'));
const MatchDetailsPage = lazy(() => import('@/pages/Matches/MatchDetailsPage'));
const MatchEditPage = lazy(() => import('@/pages/Matches/MatchEditPage'));
const GroundListPage = lazy(() => import('@/pages/Grounds/GroundListPage'));
const GroundCreatePage = lazy(() => import('@/pages/Grounds/GroundCreatePage'));
const GroundDetailsPage = lazy(() => import('@/pages/Grounds/GroundDetailsPage'));
const GroundEditPage = lazy(() => import('@/pages/Grounds/GroundEditPage'));
const ReportsPage = lazy(() => import('@/pages/Reports/ReportsPage'));
const GalleryLayout = lazy(() => import('@/pages/Gallery/GalleryLayout'));
const GalleryPage = lazy(() => import('@/pages/Gallery/GalleryPage'));
const GalleryAlbumsPage = lazy(() => import('@/pages/Gallery/GalleryAlbumsPage'));
const GalleryUploadPage = lazy(() => import('@/pages/Gallery/GalleryUploadPage'));
const GalleryAlbumPage = lazy(() => import('@/pages/Gallery/GalleryAlbumPage'));
const SponsorListPage = lazy(() => import('@/pages/Sponsors/SponsorListPage'));
const SponsorCreatePage = lazy(() => import('@/pages/Sponsors/SponsorCreatePage'));
const SponsorDetailsPage = lazy(() => import('@/pages/Sponsors/SponsorDetailsPage'));
const SponsorEditPage = lazy(() => import('@/pages/Sponsors/SponsorEditPage'));
const SettingsPage = lazy(() => import('@/pages/Settings/SettingsPage'));
const SettingsProfilePage = lazy(() => import('@/pages/Settings/SettingsProfilePage'));
const SettingsUsersPage = lazy(() => import('@/pages/Settings/SettingsUsersPage'));
const SettingsRolesPage = lazy(() => import('@/pages/Settings/SettingsRolesPage'));
const SettingsPreferencesPage = lazy(() => import('@/pages/Settings/SettingsPreferencesPage'));
const SettingsSecurityPage = lazy(() => import('@/pages/Settings/SettingsSecurityPage'));
const SettingsNotificationsPage = lazy(() => import('@/pages/Settings/SettingsNotificationsPage'));
const NotificationsPage = lazy(() => import('@/pages/Notifications/NotificationsPage'));
const LiveScorePage = lazy(() => import('@/pages/LiveScore/LiveScorePage'));
const ScorerPanelPage = lazy(() => import('@/pages/Scorer/ScorerPanelPage'));
const LEDScoreboardPage = lazy(() => import('@/pages/LED/LEDScoreboardPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFound/NotFoundPage'));

function TournamentsPortal() {
  return (
    <PortalTournamentsRoute
      publicComponent={PublicTournamentsPage}
      adminComponent={TournamentListPage}
    />
  );
}

function TeamsPortal() {
  return <PortalTeamsRoute publicComponent={PublicTeamsPage} adminComponent={TeamListPage} />;
}

function PlayersPortal() {
  return <PortalPlayersRoute publicComponent={PublicPlayersPage} adminComponent={PlayerListPage} />;
}

function GroundsPortal() {
  return <PortalGroundsRoute publicComponent={PublicGroundsPage} adminComponent={GroundListPage} />;
}

function GalleryPortal() {
  return (
    <PortalGalleryRoute publicComponent={PublicGalleryPage} adminComponent={GalleryPage} />
  );
}

function GalleryAlbumPortal() {
  return (
    <PortalGalleryRoute publicComponent={PublicAlbumPage} adminComponent={GalleryAlbumPage} />
  );
}

function SponsorsPortal() {
  return (
    <PortalSponsorsRoute publicComponent={PublicSponsorsPage} adminComponent={SponsorListPage} />
  );
}

export const publicRoutes = [
  { path: PUBLIC_ROUTES.HOME, Component: HomePage },
  { path: PUBLIC_ROUTES.ABOUT, Component: AboutPage },
  { path: PUBLIC_ROUTES.TOURNAMENTS, Component: TournamentsPortal },
  { path: PUBLIC_ROUTES.TOURNAMENT, Component: PublicTournamentPage },
  { path: PUBLIC_ROUTES.FIXTURES, Component: PublicFixturesPage },
  { path: PUBLIC_ROUTES.RESULTS, Component: PublicResultsPage },
  { path: PUBLIC_ROUTES.LIVE, Component: PublicLivePage },
  { path: PUBLIC_ROUTES.TEAMS, Component: TeamsPortal },
  { path: PUBLIC_ROUTES.TEAM, Component: PublicTeamProfilePage },
  { path: PUBLIC_ROUTES.PLAYERS, Component: PlayersPortal },
  { path: PUBLIC_ROUTES.PLAYER, Component: PublicPlayerProfilePage },
  { path: PUBLIC_ROUTES.GROUNDS, Component: GroundsPortal },
  { path: PUBLIC_ROUTES.GROUND, Component: PublicGroundDetailsPage },
  { path: PUBLIC_ROUTES.GALLERY, Component: GalleryPortal },
  { path: PUBLIC_ROUTES.GALLERY_ALBUM, Component: GalleryAlbumPortal },
  { path: PUBLIC_ROUTES.SPONSORS, Component: SponsorsPortal },
  { path: PUBLIC_ROUTES.CONTACT, Component: ContactPage },
  { path: PUBLIC_ROUTES.PRIVACY, Component: PrivacyPage },
  { path: PUBLIC_ROUTES.TERMS, Component: TermsPage },
  { path: PUBLIC_ROUTES.MATCH, Component: PublicMatchPage },
  { path: ROUTES.DESIGN_SYSTEM, Component: DesignSystemPage },
];

const PORTAL_ROUTE_PATHS = new Set([
  PUBLIC_ROUTES.TOURNAMENTS,
  PUBLIC_ROUTES.TEAMS,
  PUBLIC_ROUTES.PLAYERS,
  PUBLIC_ROUTES.GROUNDS,
  PUBLIC_ROUTES.GALLERY,
  PUBLIC_ROUTES.GALLERY_ALBUM,
  PUBLIC_ROUTES.SPONSORS,
]);

export const portalRoutes = publicRoutes.filter(({ path }) => PORTAL_ROUTE_PATHS.has(path));

export const publicWebsiteRoutes = publicRoutes.filter(({ path }) => !PORTAL_ROUTE_PATHS.has(path));

export const authRoutes = [{ path: ROUTES.LOGIN, Component: LoginPage }];

export const adminRoutes = [
  { path: ROUTES.DASHBOARD, Component: DashboardPage },
  { path: ROUTES.TOURNAMENT_CREATE, Component: TournamentWizardPage },
  { path: ROUTES.TOURNAMENT_DETAILS, Component: TournamentDetailsPage },
  { path: ROUTES.TOURNAMENT_EDIT, Component: TournamentEditPage },
  { path: ROUTES.TOURNAMENT_SETTINGS, Component: TournamentSettingsPage },
  { path: ROUTES.TEAM_CREATE, Component: TeamWizardPage },
  { path: ROUTES.TEAM_DETAILS, Component: TeamDetailsPage },
  { path: ROUTES.TEAM_EDIT, Component: TeamEditPage },
  { path: ROUTES.TEAM_STATISTICS, Component: TeamStatisticsPage },
  { path: ROUTES.TEAM_GALLERY, Component: TeamGalleryPage },
  { path: ROUTES.PLAYER_CREATE, Component: PlayerCreatePage },
  { path: ROUTES.PLAYER_DETAILS, Component: PlayerDetailsPage },
  { path: ROUTES.PLAYER_EDIT, Component: PlayerEditPage },
  { path: ROUTES.PLAYER_STATISTICS, Component: PlayerStatisticsPage },
  { path: ROUTES.MATCHES, Component: MatchListPage },
  { path: ROUTES.MATCH_CREATE, Component: MatchCreatePage },
  { path: ROUTES.MATCH_DETAILS, Component: MatchDetailsPage },
  { path: ROUTES.MATCH_EDIT, Component: MatchEditPage },
  { path: ROUTES.GROUND_CREATE, Component: GroundCreatePage },
  { path: ROUTES.GROUND_DETAILS, Component: GroundDetailsPage },
  { path: ROUTES.GROUND_EDIT, Component: GroundEditPage },
  { path: ROUTES.REPORTS, Component: ReportsPage },
  { path: ROUTES.NOTIFICATIONS, Component: NotificationsPage },
  {
    path: ROUTES.GALLERY,
    Component: GalleryLayout,
    children: [
      { path: 'albums', Component: GalleryAlbumsPage },
      { path: 'upload', Component: GalleryUploadPage },
    ],
  },
  { path: ROUTES.SPONSOR_CREATE, Component: SponsorCreatePage },
  { path: ROUTES.SPONSOR_DETAILS, Component: SponsorDetailsPage },
  { path: ROUTES.SPONSOR_EDIT, Component: SponsorEditPage },
  {
    path: ROUTES.SETTINGS,
    Component: SettingsPage,
    children: [
      { path: 'profile', Component: SettingsProfilePage },
      { path: 'users', Component: SettingsUsersPage },
      { path: 'roles', Component: SettingsRolesPage },
      { path: 'preferences', Component: SettingsPreferencesPage },
      { path: 'notifications', Component: SettingsNotificationsPage },
      { path: 'security', Component: SettingsSecurityPage },
    ],
  },
  { path: ROUTES.LIVE_SCORE, Component: LiveScorePage },
];

export const scorerRoutes = [
  { path: ROUTES.SCORER_PANEL, Component: ScorerPanelPage },
  { path: ROUTES.LED_SCOREBOARD, Component: LEDScoreboardPage },
];

export { NotFoundPage, PublicNotFoundPage };
